import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Edit2, Trash2, Shield, ShieldOff, BarChart3, Hash, User } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitersManagement = ({ token }) => {
  const [waiters, setWaiters] = useState([]);
  const [report, setReport] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingWaiter, setEditingWaiter] = useState(null);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchWaiters(); fetchReport(); }, []);

  const fetchWaiters = async () => {
    try {
      const res = await axios.get(`${API}/admin/waiters`, { headers });
      setWaiters(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/admin/waiter-report`, { headers });
      setReport(res.data);
    } catch {}
  };

  const handleSubmit = async () => {
    if (!name.trim() || !pin.trim()) { toast.error('Isim ve PIN gerekli'); return; }
    if (pin.length < 4) { toast.error('PIN en az 4 haneli olmali'); return; }
    try {
      if (editingWaiter) {
        await axios.put(`${API}/admin/waiters/${editingWaiter.id}`, { name, pin }, { headers });
        toast.success('Garson guncellendi');
      } else {
        await axios.post(`${API}/admin/waiters`, { name, pin }, { headers });
        toast.success('Garson eklendi');
      }
      setShowForm(false);
      setEditingWaiter(null);
      setName('');
      setPin('');
      fetchWaiters();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
  };

  const handleEdit = (w) => {
    setEditingWaiter(w);
    setName(w.name);
    setPin(w.pin);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu garsonu silmek istediginize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/waiters/${id}`, { headers });
      toast.success('Garson silindi');
      fetchWaiters();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
  };

  const toggleActive = async (w) => {
    try {
      await axios.put(`${API}/admin/waiters/${w.id}`, { active: !w.active }, { headers });
      toast.success(w.active ? 'Garson pasif yapildi' : 'Garson aktif yapildi');
      fetchWaiters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
  };

  if (loading) return <div className="text-center py-8">Yukleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between">
        <h2 className="font-playfair text-xl font-semibold text-charcoal">Garson Yonetimi</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowReport(true)} variant="outline" size="sm" data-testid="waiter-report-btn">
            <BarChart3 className="w-4 h-4 mr-1" />Performans
          </Button>
          <Button onClick={() => { setEditingWaiter(null); setName(''); setPin(''); setShowForm(true); }}
            className="bg-basil text-white hover:bg-basil/90" size="sm" data-testid="add-waiter-btn">
            <UserPlus className="w-4 h-4 mr-1" />Garson Ekle
          </Button>
        </div>
      </div>

      {/* Waiters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {waiters.map(w => {
          const stats = report.find(r => r.waiter_id === w.id);
          return (
            <motion.div key={w.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className={`bg-white rounded-xl p-4 shadow-sm border-2 transition-all ${w.active ? 'border-basil/20' : 'border-stone-200 opacity-60'}`}
              data-testid={`waiter-card-${w.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${w.active ? 'bg-basil' : 'bg-stone-400'}`}>
                    {w.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-charcoal">{w.name}</p>
                    <p className="text-xs text-charcoal/50 flex items-center gap-1"><Hash className="w-3 h-3" />PIN: {w.pin}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${w.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {w.active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              {stats && (
                <div className="bg-sand rounded-lg p-3 mb-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold text-charcoal">{stats.order_count}</p>
                    <p className="text-[10px] text-charcoal/50">Siparis</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-terracotta">{'\u20AC'}{stats.total_sales.toFixed(0)}</p>
                    <p className="text-[10px] text-charcoal/50">Satis</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-charcoal">{stats.total_items}</p>
                    <p className="text-[10px] text-charcoal/50">Urun</p>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => toggleActive(w)} size="sm" variant="outline" className="flex-1 text-xs"
                  data-testid={`waiter-toggle-${w.id}`}>
                  {w.active ? <><ShieldOff className="w-3 h-3 mr-1" />Pasif Yap</> : <><Shield className="w-3 h-3 mr-1" />Aktif Yap</>}
                </Button>
                <Button onClick={() => handleEdit(w)} size="sm" variant="outline" data-testid={`waiter-edit-${w.id}`}>
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button onClick={() => handleDelete(w.id)} size="sm" variant="outline" className="text-red-500 hover:text-red-700"
                  data-testid={`waiter-delete-${w.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {waiters.length === 0 && (
        <div className="text-center py-12 text-charcoal/60">
          <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Henuz garson eklenmemis</p>
          <p className="text-sm mt-1">Garson ekleyerek siparis takibini baslatin</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingWaiter ? 'Garson Duzenle' : 'Yeni Garson Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-charcoal">Garson Adi</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ornek: Ahmet"
                className="mt-1" data-testid="waiter-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">PIN Kodu (min 4 hane)</label>
              <Input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="Ornek: 1234"
                className="mt-1" maxLength={6} data-testid="waiter-pin-input" />
            </div>
            <Button onClick={handleSubmit} className="w-full bg-basil text-white hover:bg-basil/90"
              data-testid="waiter-form-submit">
              {editingWaiter ? 'Guncelle' : 'Ekle'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Performance Report Dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Garson Performans Raporu</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {report.length === 0 ? (
              <p className="text-center text-charcoal/50 py-4">Henuz veri yok</p>
            ) : (
              <div className="divide-y">
                {report.map((r, i) => (
                  <div key={r.waiter_id} className="flex items-center gap-3 py-3" data-testid={`report-row-${r.waiter_id}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${i === 0 ? 'bg-terracotta' : i === 1 ? 'bg-basil' : 'bg-stone-400'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-charcoal">{r.waiter_name}</p>
                      <p className="text-xs text-charcoal/50">{r.active ? 'Aktif' : 'Pasif'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-terracotta">{'\u20AC'}{r.total_sales.toFixed(2)}</p>
                      <p className="text-xs text-charcoal/50">{r.order_count} siparis / {r.total_items} urun</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default WaitersManagement;
